import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import { shallowEqual, useSelector } from "umi";
import PerformanceMetricsTable, { MetricsTableColumn } from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/components/OverallTableCo/components/SelectShowValueType";
import SelectorDimensionRadio from "@/pages/dashboard/components/OverallTableCo/components/SelectorDimensionRadio";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";
import {
  useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useMetricsColumns,
  useCustomSelectors,
} from "./hooks";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import useData from "./useData";
import { MetricsSettingsProp, TableProps } from "./types";
import { useLocal, useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { SortBy } from "tea-component/lib/table/addons";
import { PagingQuery } from "tea-component";
import { Option } from "@/pages/dashboard/components/DownloadFormatModal/types";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";
import moment from 'moment';
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from '../DownloadFormatModal';
import { formatProps, handleAccumulatedMetrics, handleMetricsKeyNotByDate, getMultiSortParams, buildDataSourceCustomFiltersMerge, buildDataCustomFiltersRequestKey } from './utils';
import { selectValByCondition } from "@home/utils/common";
import '@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss';
import { formatFilterValues, otherParamsVal } from "../../utils/utils";
import { getExplainVal } from "../../utils/getExplainVal";
import {
  filterComponentMetricsByTableMetric,
  resolveAndSelectName,
  resolveConfigString,
} from '../../utils/metricUtils';
import { isRateSort } from "../PerformanceMetricsTable/components/_utils";
import AIAnalysisButton from "@/pages/dashboard/components/AIAnalysisButton";
import { buildDimensionMetricsAndAppendToMap } from "@/pages/dashboard/utils/dimensionMetricsUtils";

export default function OverallTableCo(tableProps: TableProps) {
  const props = formatProps(tableProps);
  const { config, className, style, filterVal: propsFilterValue, filterLoading: propsFilterLoading, isError: propsIsError, beforeTableSolt, data: propsData, logConfig } = props || {};
  const {
    componentMetrics: propsComponentMetrics,
    titleEn,
    titleCn,
    secTitleEn,
    secTitleCn,
    showStatistic: propsShowStatistic,
    slotConfig,
    valueTypeConfig,
    dimensionConfig,
    settingsConfig,
    downConfig,
    titleConfig,
    componentMetricsConfig,
    isHaveDiff = false,
    otherParams: otherParamsData,
    pageSizeOptions = [10, 20, 30, 40, 50, 100, 200],
    defaultSort = [],
    copyTitleList,
    hasDateColumn = true,
    customColumnsBeforeDim,
    customColumnsAfterDim,
    customColumnsAfterMetrics,
    customDimension,
    isNewSort,
    filterConfig,
    lockColumns,
    isUseNewSortParams,
    getParams,
    noResetSort, // fix：不重置回默认排序
  } = config || {};
  const {
    isShowValueType = true,
    isShowMetricsSettings = true,
    isShowProgressBar = true,
    isShowdownloadConfig = true,
    defaultProgressVisible = true
  } = slotConfig || {};
  const { valueType = 'value_only', isShowDod = true, isShowSdlw = true, isShowChange = true } = valueTypeConfig || {};
  const {
    dateDisabled = true,
    defaultAccumulation = ['date'],
    isShowCountryFlag = false,
    isRevenueCustomAccumulate,
    filterDimsAndColumns,
    isAutoSort,
    dimensionInfoMap,
    showDimList = true,
    dimensionOptions,
  } = dimensionConfig || {};

  // customSelectorsDimensions 通用处理：拆分列 / 聚合 / UI 维度，并维护 selector 选中值
  const {
    selectorDimensions: normalizedSelectorDimensions,
    selectorColumnKeys,
    selectorColumnKeysVisible,
    includeCustomAccumulationKeys,
    selectorDimensionsForUi,
    selectorValues,
    setSelectorValues,
    selectorValuesRef,
    buildCustomFiltersMerge,
  } = useCustomSelectors(config?.customFiltersConfig?.customSelectorsDimensions);
  const customFiltersFromData = config?.customFiltersConfig?.customFiltersFromData;
  const { showTypeOptions } = settingsConfig || {};
  const { 
    showModalDataStat, 
    showModalData, 
    customDownNameEn, 
    customDownNameCn, 
    defaultFileType, 
    isOnlyDown,
    requiredMetricCountToDownload,
  } = downConfig || {};
  const { isEN, locale, formatMessage } = useLocal();
  const contextObj = useContext(FilterContext);
  const { filterVal: contextFilterValue, filterLoading: contextFilterLoading } = contextObj || {};
  const resolvedOtherParams = useMemo(
    () => otherParamsVal(otherParamsData, formatFilterValues(contextFilterValue)) || {},
    [otherParamsData, contextFilterValue],
  );
  // 优先取传入的filter
  const filterVal = (propsFilterValue !== undefined) ? propsFilterValue : contextFilterValue;
  const filterLoading = (propsFilterLoading !== undefined) ? propsFilterLoading : contextFilterLoading;

  const { timeType = 'Day', filterDefaultSelect, pagePath } = filterVal || {};
  const { explainInfo, gameCode, selector } = useSelector(
    (state: any) => ({
      explainInfo: state.dashboard?.explainInfo,
      gameCode: state.dashboard?.currentGame?.game_code,
      selector: state?.dashboardSetting?.selector ?? {},
    }),
    shallowEqual
  );
  const [columns, setColumns] = useState<any[]>([]);
  const defaultValueType = 'value_only';
  const [selectType, setSelectType] = useState<ShowValueType>(valueType || defaultValueType);
  const [showStatistic, setShowStatistic] = useState<string[]>(propsShowStatistic || defaultStatisticsTypeValue);
  const [accumulated, setAccumulated] = useState<string[]>(defaultAccumulation);
  const [isCallingRateSortApi, setIsCallingRateSortApi] = useState<boolean>(false);

  const customFiltersMerge = useMemo(
    () => buildCustomFiltersMerge(selectorValues),
    [buildCustomFiltersMerge, selectorValues],
  );

  const dataCustomFiltersRequestKey = useMemo(
    () => buildDataCustomFiltersRequestKey(propsData, customFiltersFromData),
    [propsData, customFiltersFromData],
  );

  const dataSourceCustomFiltersMerge = useMemo(
    () => buildDataSourceCustomFiltersMerge(propsData, customFiltersFromData),
    [dataCustomFiltersRequestKey, customFiltersFromData],
  );

  const effectiveOtherParams = useMemo(() => {
    const customFiltersMergeList = [
      ...(dataSourceCustomFiltersMerge || []),
      ...(customFiltersMerge || []),
    ];
    return {
      ...(customFiltersMergeList.length ? { custom_filters_merge: customFiltersMergeList } : {}),
      ...resolvedOtherParams,
    };
  }, [resolvedOtherParams, customFiltersMerge, dataSourceCustomFiltersMerge]);

  // 选择器维度配置（仅展示 showSelector 的维度）已由 useCustomSelectors 返回的 selectorDimensionsForUi 提供
  
  // 解决columns在其他函数调用时，闭包问题，导致column拿到最新问题
  const columnsRef = useRef<any[]>([]);

  const { title, secTitle, componentMetrics } = useMemo(() => {
    const defaultTitle = formatMessage({ id: 'overview_Overall_Performance' });
    // eslint-disable-next-line no-nested-ternary
    const singleTitle = selectValByCondition(isEN, titleEn, titleCn) || defaultTitle;
    const secTitle = selectValByCondition(isEN, secTitleEn, secTitleCn);
    const multiTitle = isEN ? titleConfig?.[timeType]?.titleEn : titleConfig?.[timeType]?.titleCn;

    // 获取基础指标列表
    const baseMetrics = componentMetricsConfig ? componentMetricsConfig?.[timeType] : propsComponentMetrics;

    // 根据 tableMetric 配置筛选指标
    const filteredMetrics = filterComponentMetricsByTableMetric(
      baseMetrics,
      propsData?.tableMetric,
      timeType,
    );

    return {
      title: titleConfig ? multiTitle : singleTitle,
      secTitle,
      componentMetrics: filteredMetrics
    };
  }, [isEN, config, timeType, propsData?.tableMetric]);

  // 取消by date分日期操作
  const { hasDate, isAccumulated, metricsList } = useMemo(() => {
    const { hasDate = true, isAccumulated = false, metricsList = componentMetrics } = handleAccumulatedMetrics({ filterVal, isHaveDiff, componentMetrics, accumulated });
    return {
      hasDate,
      isAccumulated,
      metricsList,
    };
  }, [accumulated, componentMetrics, filterVal, isHaveDiff]);
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal, false, false, filterDefaultSelect, pagePath, true, dimensionInfoMap, dimensionOptions);

  SEPARATED_OPTIONS = (SEPARATED_OPTIONS || []).filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: dateDisabled
  });

  // 过滤不展示的维度
  if (filterDimsAndColumns) {
    SEPARATED_OPTIONS = SEPARATED_OPTIONS.filter((item: any) => !filterDimsAndColumns.includes(item.value));
  }
  
  const compData = useData(
    filterVal,
    filterLoading,
    selectType,
    config,
    isAccumulated ? metricsList : componentMetrics,
    effectiveOtherParams,
    lockColumns,
    columns,
    isUseNewSortParams,
    includeCustomAccumulationKeys,
    normalizedSelectorDimensions,
    dataCustomFiltersRequestKey,
  );

  const {
    loading,
    isError,
    paging,
    setPaging,
    sort,
    setSort,
    total,
    tableData,
    statisticsData,
    filterValueSort,
    accumulateOldValueRef,
    getRequestParams,
    getData,
    downLoadData,
    frontLogicalFlag,
    overLimitBarTriggerKey
  } = compData || {};
  // 列定义, 中英文切换(全部), Accumulate关闭(仅仅维度列),
  const COLUMN_CONFIG = useMetricsColumns(isAccumulated ? metricsList : componentMetrics || []);

  // 默认展示的字段
  const defaultMetricsList = Object.keys(COLUMN_CONFIG).filter(item => !COLUMN_CONFIG?.[item]?.metricsConfig?.defaultHidden);
  const [showMetrics, setShowMetrics] = useState(defaultMetricsList);
  const { DATE_COLUMN, getAccumulateColumns, getMetricColumns, getCustomColumns, getSelectorDimensionColumn } = useTableColumns({ isAccumulated, config, hasDate, timeType });

  // 准备 metricsData，用于解析模板字符串
  const metricsData = useMemo<Record<string, any>>(() => {
    return { metricsInfo: propsData?.metricsInfo };
  }, [propsData?.metricsInfo]);

  useEffect(() => {
    // 当单日取消分日期时是没有sdlw的，设置回默认
    if (isAccumulated && selectType === 'sdlw') {
      setSelectType(defaultValueType);
    }

    setShowMetrics(handleMetricsKeyNotByDate(componentMetrics, showMetrics, isAccumulated, timeType));
  }, [isAccumulated, timeType]);

  // useEffect(() => {
  // 	const columns = [
  // 		DATE_COLUMN,
  // 		...getAccumulateColumns(accumulateOldValueRef?.current, isShowCountryFlag, isRevenueCustomAccumulate, gameCode, selector),
  // 		...getMetricColumns(showMetrics, COLUMN_CONFIG, rainbow, selectType, pcConsoleRainbow)
  // 	];

  // 	setColumns(columns);
  // }, [accumulateOldValueRef.current, selectType, timeType, showMetrics, locale, contextObj]);
  useEffect(() => {
    // 日期维度
    let columns: any = [];
    if (hasDateColumn) {
      columns.push(DATE_COLUMN);
    }
    // 自定义前置
    if (customColumnsBeforeDim && customColumnsBeforeDim?.length > 0) {
      columns.push(...getCustomColumns(customColumnsBeforeDim, explainInfo, paging));
    }
    // customSelectorsDimensions 维度列：固定在左侧，排在日期/前置之后、其他维度之前（选「全部」时不展示该列）
    if (selectorColumnKeysVisible.length > 0) {
      selectorColumnKeysVisible.forEach(dimKey => {
        const dimConfig = normalizedSelectorDimensions.find(d => d.key === dimKey);
        const dimColConfig = customColumnsAfterDim?.find((col: any) => col.columnKey === dimKey);
        const header = (() => {
          if (dimConfig?.labelEn && dimConfig?.labelCn) return isEN ? dimConfig.labelEn : dimConfig.labelCn;
          return dimKey;
        })();
        let dimCol: any;
        if (dimColConfig) {
          const [firstCol] = getCustomColumns([{ ...dimColConfig, fixed: 'left' }], explainInfo, paging);
          dimCol = firstCol;
        } else {
          dimCol = getSelectorDimensionColumn(dimKey, header, 120);
        }
        if (dimCol) columns.push(dimCol);
      });
    }
    // 筛选器维度（用户勾选的维度列，排在 Ad Format 后、指标前）
    columns.push(...getAccumulateColumns(
      accumulated,
      explainInfo,
      isShowCountryFlag,
      isRevenueCustomAccumulate,
      gameCode,
      selector,
    ));
    // 自定义维度列
    if (customDimension && customDimension?.length > 0) {
      columns.push(...getCustomColumns(customDimension, explainInfo, paging));
    }
    // 自定义后置（排除 customSelectorsDimensions，已在上方固定位置插入）
    if (customColumnsAfterDim && customColumnsAfterDim?.length > 0) {
      const customCols = getCustomColumns(
        customColumnsAfterDim.filter((col: any) => !selectorColumnKeys.includes(col.columnKey)),
        explainInfo,
        paging
      );
      if (customCols?.length > 0) {
        columns.push(...customCols);
      }
    }
    // 指标
    columns.push(...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType, metricsData));
    // 自定义后置
    if (customColumnsAfterMetrics && customColumnsAfterMetrics?.length > 0) {
      columns.push(...getCustomColumns(customColumnsAfterMetrics, explainInfo, paging));
    }

    // 过滤不展示的字段（若在 customSelectorsDimensions 中声明为 showColumn 且非「全部」选中则保留）
    if (filterDimsAndColumns) {
      columns = columns?.filter((item: any) =>
        !filterDimsAndColumns?.includes(item.key) || selectorColumnKeysVisible.includes(item.key)
      );
    }

    // 配置锁住列（Ad Format 列仅 fixed: 'left' 固定滚动位置，不参与锁定逻辑、锁图标、多列排序等）
    if (lockColumns && lockColumns.length > 0) {
      columns = columns?.map((item: any) => {
        if (selectorColumnKeysVisible.includes(item.key)) {
          return item;
        }
        const lockColumn = lockColumns?.find((col: any) => col.key === item.key);
        if (lockColumn) {
          const dimList = accumulated || [];
          const { key, lockTipEn, lockTipCn, unlockTipEn, unlockTipCn, lockRainbowKey = '', unlockRainbowKey = '', defaultLock, showDimList } = lockColumn || {};
          const oldLockColumn = columnsRef.current?.find((c: any) => c.key === key) || {};
          return {
            ...item, lock: oldLockColumn.lock ?? defaultLock,
            hasLock: showDimList ? dimList?.some((dim: string) => showDimList?.includes(dim)) : defaultLock,
            lockTip: getExplainVal(explainInfo, lockRainbowKey, locale, lockTipEn, lockTipCn),
            unlockTip: getExplainVal(explainInfo, unlockRainbowKey, locale, unlockTipEn, unlockTipCn),
          };
        }
        return item;
      });
      columnsRef.current = columns;
    }
    setColumns(columns);
  // 优化：使用 propsData?.metricsInfo 替代整个 metricsData 对象，减少不必要的重新计算
  }, [accumulated, selectType, timeType, showMetrics, locale, contextObj, paging, propsData?.metricsInfo, selectorColumnKeys, selectorColumnKeysVisible, normalizedSelectorDimensions, customColumnsAfterDim]);

  useEffect(() => {
    // 多粒度筛选器更改设置展示指标
    componentMetricsConfig && setShowMetrics(Object.keys(COLUMN_CONFIG));
  }, [timeType]);

  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, timeType, false, undefined, metricsData),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic, false, metricsData)
  ];

  const copyData = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, timeType, true, undefined, metricsData),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic, true, metricsData)
  ];

  // 下载弹窗指标options - 兼容模板字符串
  const metricOptions: Option[] = useMemo(() => Object.keys(COLUMN_CONFIG).map(key => {
    const config = COLUMN_CONFIG[key]?.metricsConfig;
    // 兼容模板字符串格式，如 "{{metricsInfo.total_users.en}}"
    const name = resolveAndSelectName(isEN, config?.enName, config?.cnName, propsData?.metricsInfo) || '';
    return { value: key, text: name };
  }), [COLUMN_CONFIG, isEN, propsData?.metricsInfo]);

  const onSortChange = (sorts: SortBy[] = []) => {
    // 排序
    let sort = sorts;
    if (sort?.length === 0 && !noResetSort) {
      // 恢复默认排序时，优先取自定义
      sort = defaultSort;
    }

    setSort(sort);
    // 发起请求（页面数据请求传参不变）
    const params = getRequestParams(filterValueSort?.current?.paging, sort, filterValueSort?.current?.filterValue,
      { ...effectiveOtherParams, multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort?.[0], selectType, isUseNewSortParams) });
    // forceUpdate()
    getData(params);
    setTimeout(() => {
      setIsCallingRateSortApi(false);
    }, 0);
  };

  const onSortChangeRef = useRef<(sorts?: SortBy[]) => void>(() => {});
  onSortChangeRef.current = onSortChange;

  const getExtractMetrics = (configMetrisArr: ComponentMetricsItemType[], metricsArr: any[]) => configMetrisArr.filter(item => metricsArr.includes(item.reqMetricKey));

  const downloadConfig = {
    defaultValue: showMetrics,
    onDownload: ({ isDownloadStatistics, metrics, dataType, fileFormat, selectorValues: downloadSelectorValues }: {
      isDownloadStatistics: boolean;
      metrics: string[];
      dataType: string;
      fileFormat: string;
      selectorValues?: Record<string, string>;
    }) => {
      const metricNameArr = getExtractMetrics(isAccumulated ? metricsList : componentMetrics || [], metrics);
      // 后续下载要给后端传递对应指标的中英文名称，此处提前处理
      // : { [key: string]: { en: string; zh: string; type: any; }; }
      const metricsMap: { [key: string]: { en: string; zh: string; type: any; }; } = {};
      metricNameArr?.forEach((item: any) => {
        // 兼容模板字符串格式
        const enName = resolveConfigString(item?.enName, propsData?.metricsInfo) || '';
        const cnName = resolveConfigString(item?.cnName, propsData?.metricsInfo) || '';
        metricsMap[item?.reqMetricKey] = {
          en: enName || '',
          zh: cnName || '',
          type: item?.type || item?.downLoadFormatRule
        };
      });
      
      // 处理下载时的 dimension_metrics 参数和 metrics_map（按 customSelectorsDimensions 通用生成）
      const finalSelectorValues = downloadSelectorValues || selectorValuesRef.current || {};
      // 下载时 filters.custom_filters 使用弹窗内部的 selector 选中值
      const downloadCustomFiltersMerge = buildCustomFiltersMerge(finalSelectorValues);
      const downloadCustomFiltersMergeList = [
        ...(dataSourceCustomFiltersMerge || []),
        ...(downloadCustomFiltersMerge || []),
      ];

      // dimension_metrics 按页面 columns 实际顺序提取（isDimColumn 且排除 date），并把维度中英文补到 metricsMap
      const currentColumns = columnsRef.current?.length ? columnsRef.current : columns;
      const dimensionMetrics = buildDimensionMetricsAndAppendToMap(
        currentColumns,
        metricsMap,
        normalizedSelectorDimensions,
        finalSelectorValues,
      );
      
      let params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
        metrics,
        metrics_map: metricsMap,
        show_dod: dataType === 'show_dod',
        show_sdlw: dataType === 'show_sdlw',
        file_format: fileFormat,
        aggregates: isDownloadStatistics ? showStatistic : [],
        multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort?.[0], selectType, isUseNewSortParams),
        ...(dimensionMetrics.length > 0 ? { dimension_metrics: dimensionMetrics } : {}),
        ...resolvedOtherParams,
        ...(downloadCustomFiltersMergeList.length ? { custom_filters_merge: downloadCustomFiltersMergeList } : {}),
      });
      if (typeof getParams === 'function') {
        params = getParams(params, true);
      }
      const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
      return downLoadData(api, params);
    },
    timeType,
    metricOptions,
    showData: { valueOnly: true, showDod: true, showSdlw: !isAccumulated },
    showModalDataStat,
    showModalData,
    customDownName: selectValByCondition(isEN, customDownNameEn, customDownNameCn),
    defaultFileType,
    isOnlyDown,
    requiredMetricCountToDownload,
    // customSelectorsDimensions：用于下载弹窗展示多维度单选
    ...(selectorDimensionsForUi.length > 0 ? {
      customSelectorsDimensions: selectorDimensionsForUi.map(d => ({
        key: d.key,
        labelEn: d.labelEn,
        labelCn: d.labelCn,
        options: d.options || [],
        defaultValue: d.defaultValue,
      })),
      defaultSelectorValues: selectorValues,
    } : {}),
  };

  const { setNoticeStatus } = useNotificationOfDownloadTable(title);
  function downPromise() {
    const metricNameArr = getExtractMetrics(isAccumulated ? metricsList : componentMetrics || [], showMetrics);
    // : { [key: string]: { en: string; zh: string; type: any}}
    const metricsMap: { [key: string]: { en: string; zh: string; type: any; }; } = {};
    metricNameArr?.forEach((item: any) => {
      // 兼容模板字符串格式
      const enName = resolveConfigString(item?.enName, propsData?.metricsInfo) || '';
      const cnName = resolveConfigString(item?.cnName, propsData?.metricsInfo) || '';
      metricsMap[item?.reqMetricKey] = {
        en: enName || '',
        zh: cnName || '',
        type: item?.type || item?.downLoadFormatRule
      };
    });
    
    // dimension_metrics 按页面 columns 实际顺序提取（isDimColumn 且排除 date），并把维度中英文补到 metricsMap
    const currentColumns = columnsRef.current?.length ? columnsRef.current : columns;
    const finalSelectorValues = selectorValuesRef.current || {};
    const dimensionMetrics = buildDimensionMetricsAndAppendToMap(
      currentColumns,
      metricsMap,
      normalizedSelectorDimensions,
      finalSelectorValues,
    );
    
    let params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
      metrics: showMetrics,
      metrics_map: metricsMap,
      show_dod: selectType === 'dod',
      show_sdlw: selectType === 'sdlw',
      show_change: selectType === 'change',
      file_format: defaultFileType || 'excel',
      aggregates: showStatistic,
      multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort?.[0], selectType, isUseNewSortParams),
      ...(dimensionMetrics.length > 0 ? { dimension_metrics: dimensionMetrics } : {}),
      ...effectiveOtherParams,
    });
    if (typeof getParams === 'function') {
      params = getParams(params, true);
    }
    const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
    return downLoadData(api, params);
  }
  // 下载
  function handleDownload() {
    const downloadPromise = downPromise?.();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (downloadPromise) {
      // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus('loading');
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${(downloadConfig.customDownName || title)?.replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus('success');
            },
            onDownloadError() {
              setNoticeStatus('error');
            }
          }, defaultFileType || 'excel');
        } catch (e) {
          setNoticeStatus('error');
        }
      })();
    }
  }

  // 锁住状态变化触发
  const changeLock = (column: MetricsTableColumn) => {
    const newColumns = columns.map(item => {
      if (item.key === column.key) {
        return {
          ...item,
          lock: !column.lock
        };
      }
      return item;
    });
    columnsRef.current = newColumns;
    setColumns(newColumns);
    // 发起请求（页面数据请求传参不变）
    const params = getRequestParams(filterValueSort?.current?.paging, sort, filterValueSort?.current?.filterValue,
      { ...effectiveOtherParams, multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort?.[0], selectType, isUseNewSortParams) });
    getData(params);
  };

  return (
    <div className={className || ''} style={style || {}}>
      <PerformanceMetricsTable
        title={title}
        secTitle={secTitle}
        columns={columns}
        records={records}
        isNewSort={isNewSort}
        copyConfig={{
          copyData,
          copyTitleList
        }}
        onLockChange={changeLock}
        beforeTableSolt={beforeTableSolt}
        hasLockColumns={lockColumns && lockColumns?.length > 0}
        selectType={selectType}
        timeType={timeType}
        sorting={{
          defaultSorts: sort,
          onSortChange
        }}
        // boxStyle解决点击时弹窗闪动问题
        filterConfig={filterConfig ? { ...(filterConfig || {}), boxStyle: { display: loading ? 'none' : 'block' } } : filterConfig}
        paging={{
          pageIndex: paging.pageIndex,
          pageSize: paging.pageSize,
          recordCount: total,
          ...(pageSizeOptions ? { pageSizeOptions } : {}),
          onPagingChange(query: PagingQuery) {
            // 切换分页
            setPaging(query);
            // 发起请求（页面数据请求传参不变）
            const params = getRequestParams(query, sort, filterValueSort?.current?.filterValue, {
              ...effectiveOtherParams,
              multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort?.[0], selectType, isUseNewSortParams)
            });
            getData(params);
          }
        }}
        topTip={{
          left: (
            <div className={showDimList || isShowValueType ? `overall-conext-margin js-overview-table-dimension` : ''}>
              {showDimList && <DimensionAccumulateRow
                options={SEPARATED_OPTIONS}
                value={accumulated}
                onChange={(value: string[]) => {
                  setAccumulated(value);
                  setPaging({ pageSize: 10, pageIndex: 1 });
                  let newSortList = defaultSort;
                  if (isAutoSort) {
                    // 获取第一列排序参数
                    newSortList = [{ "by": value[0], "order": undefined }];
                  }
                  setSort(newSortList);
                  if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                    accumulateOldValueRef.current = value;
                    // 解决因columnsRef.current会慢于change事件，导致切换分日期时，传参错误
                    const currentColumns = columnsRef.current?.filter((item: any) => (value?.includes(item.key) && item.isDimColumn) || !item.isDimColumn)?.map((item: any) => {
                      if (Object.hasOwn(item, 'hasLock')) {
                        const { showDimList, defaultLock } = lockColumns?.find(col => col.key === item.key) || {};
                        return { ...item, hasLock: showDimList ? value?.some(dim => showDimList?.includes(dim)) : defaultLock };
                      }
                      return item;
                    });
                    // 发起请求
                    const { isAccumulated, metricsList } = handleAccumulatedMetrics({ filterVal, isHaveDiff, componentMetrics, accumulated: value });
                    const list = isAccumulated ? metricsList : componentMetrics;
                    const metrics = list?.map((item: { reqMetricKey: any; }) => item?.reqMetricKey) || [];
                    // 取消分日期 metrics指标参数处理
                    const params = getRequestParams({ pageSize: 10, pageIndex: 1 }, newSortList, filterValueSort?.current?.filterValue, {
                      ...effectiveOtherParams, metrics,
                      multi_sort: getMultiSortParams(lockColumns, currentColumns, newSortList?.[0], selectType, isUseNewSortParams)
                    });
                    getData(params);
                  }
                }}
              />}
              {selectorDimensionsForUi.map(d => (
                <SelectorDimensionRadio
                  key={d.key}
                  value={selectorValues?.[d.key] ?? d.defaultValue ?? 'all'}
                  onChange={(value) => {
                    setSelectorValues(prev => ({ ...prev, [d.key]: value }));
                    setPaging({ pageSize: 10, pageIndex: 1 });
                    const nextValues = { ...selectorValuesRef.current, [d.key]: value };
                    const nextMerge = buildCustomFiltersMerge(nextValues);
                    const customFiltersMergeList = [
                      ...(dataSourceCustomFiltersMerge || []),
                      ...(nextMerge || []),
                    ];
                    const params = getRequestParams({ pageSize: 10, pageIndex: 1 }, sort, filterValueSort?.current?.filterValue, {
                      ...resolvedOtherParams,
                      ...(customFiltersMergeList.length ? { custom_filters_merge: customFiltersMergeList } : {}),
                      multi_sort: getMultiSortParams(lockColumns, columnsRef.current, sort?.[0], selectType, isUseNewSortParams)
                    });
                    getData(params);
                  }}
                  options={d.options}
                  labelEn={d.labelEn || d.key}
                  labelCn={d.labelCn || d.key}
                />
              ))}
              {isShowValueType && (
                <SelectShowValueType
                  timeType={timeType}
                  value={selectType}
                  onChange={(value) => {
                    setSelectType(value as ShowValueType);
                    if (isRateSort(sort)) {
                      setIsCallingRateSortApi(true);
                      const newDefaultSort = [...defaultSort];
                      if (newDefaultSort?.[0]?.by)
                        newDefaultSort[0].by = newDefaultSort[0].by.replace(/value_only|dod|sdlw|change/g, '');
                      setTimeout(() => {
                        if (sort?.[0].by.includes(value)) {
                          onSortChangeRef.current(value === 'value_only' ? newDefaultSort : sort);
                        }
                      }, 0);
                    }
                  }}
                  isShowDod={isShowDod}
                  isShowSdlw={isShowSdlw && !isAccumulated} // 单天不分日期时，不展示sdlw
                  isShowChange={isShowChange}
                />
              )}
            </div>
          ),
          right: (
            <>
              <AIAnalysisButton componentKey={config?.aiAnalysisKey || 'OverallTableCo'} pageId={logConfig?.pageId} agentId={config?.aiAnalysisAgentId}/>
              {isShowMetricsSettings && (
                <MetricsSettings
                  key={`MetricsSettings${timeType}`}
                  onConfirm={({ metricValues, statisticsType }: MetricsSettingsProp) => {
                    setShowStatistic(statisticsType);
                    setShowMetrics(metricValues);
                  }}
                  disabledOptions={dateDisabled ? [{ value: 'date', text: formatMessage({ id: 'common_date' }) }] : []}
                  metricOptions={metricOptions}
                  showMetrics={showMetrics}
                  showTypeOptions={showTypeOptions}
                  defaultStatisticsType={propsShowStatistic}
                />
              )}
            </>
          )
        }}
        onResizeEnd={columns => setColumns(columns)}
        isShowProgressBar={isShowProgressBar}
        loading={loading || isCallingRateSortApi}
        isError={propsIsError || isError}
        downloadConfig={isShowdownloadConfig ? (downloadConfig as any) : null}
        onDownload={handleDownload}
        defaultProgressVisible={defaultProgressVisible}
        frontLogicalFlag={frontLogicalFlag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
      />
    </div>
  );
}
